import { lazy, Suspense } from 'react';
import LoadIndicator from 'devextreme-react/load-indicator';

import styles from './dynamic-loader.module.scss';

/**
 * Dynamically loads a React component using React.lazy and Suspense.
 * Assumes the component is a named export matching the provided name.
 * Automatically wraps the loaded component in a Suspense boundary with the specified fallback.
 *
 * @param {object} options - The options object.
 * @param {string} options.name - The name of the component export to resolve.
 * @param {function(): Promise<{[key: string]: React.ComponentType<any>}>} options.importFunc - A function that calls dynamic import() for the component module.
 * @param {React.ReactNode} [options.fallback] - Optional fallback component to display while loading. Defaults to LoadIndicator.
 * @returns {React.ComponentType<any>} A React component that handles its own lazy loading and fallback display.
 */
export const dynamicLoader = ({
  name,
  importFunc,
  fallback = (
    <div className={styles.fallbackContainer}>
      <LoadIndicator />
    </div>
  ),
}) => {
  // Use React.lazy, adapting the import promise to extract the named export
  // and wrap it in an object with a `default` key for `lazy`.
  const LazyComponent = lazy(() =>
    importFunc().then((module) => ({
      default: module[name],
    }))
  );

  // Return a wrapper component that includes the Suspense boundary.
  const WrappedComponent = (props) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );

  // Assign a display name for easier debugging in React DevTools
  WrappedComponent.displayName = `dynamicLoader(${name})`;

  return WrappedComponent;
};
